import { ShopAsset, ASSET_CATEGORY_LABELS } from '@/hooks/useAssets';
import { useAssetFinancials, useAssetOpportunityCost } from '@/hooks/useAssetFinancials';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { MapPin, Hash, Building2, Calendar, DollarSign, TrendingDown } from 'lucide-react';
import { AssetFinancialSummaryCard } from './AssetFinancialSummaryCard';

interface AssetOverviewTabProps {
  asset: ShopAsset;
}

export function AssetOverviewTab({ asset }: AssetOverviewTabProps) {
  const { isManager } = usePermissions();
  const { data: financials, isLoading: loadingFinancials } = useAssetFinancials(asset.id);
  const { data: opportunityCost, isLoading: loadingOpportunity } = useAssetOpportunityCost(asset.id);

  const showOpportunityCost = isManager && opportunityCost && opportunityCost.estimated_lost_capacity_hours > 0;

  return (
    <div className="space-y-6">
      {/* Opportunity Cost Alert - Manager Only */}
      {showOpportunityCost && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <TrendingDown className="h-4 w-4" />
              Lost Capacity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-amber-600 dark:text-amber-400">Downtime</p>
                <p className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                  {opportunityCost.current_downtime_days} days
                </p>
              </div>
              <div>
                <p className="text-amber-600 dark:text-amber-400">Avg Daily Capacity</p>
                <p className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                  {opportunityCost.avg_daily_book_hours.toFixed(1)} hrs
                </p>
              </div>
              <div>
                <p className="text-amber-600 dark:text-amber-400">Est. Lost Hours</p>
                <p className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                  {opportunityCost.estimated_lost_capacity_hours.toFixed(1)} hrs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Asset Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Asset Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <p className="font-medium">{ASSET_CATEGORY_LABELS[asset.category]}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Portable</p>
              <p className="font-medium">{asset.is_portable ? 'Yes' : 'No'}</p>
            </div>
          </div>

          {asset.asset_type && (
            <div>
              <p className="text-sm text-muted-foreground">Equipment Type</p>
              <p className="font-medium">{asset.asset_type}</p>
            </div>
          )}

          {(asset.manufacturer || asset.model) && (
            <div className="grid grid-cols-2 gap-4">
              {asset.manufacturer && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    Manufacturer
                  </p>
                  <p className="font-medium">{asset.manufacturer}</p>
                </div>
              )}
              {asset.model && (
                <div>
                  <p className="text-sm text-muted-foreground">Model</p>
                  <p className="font-medium">{asset.model}</p>
                </div>
              )}
            </div>
          )}

          {asset.serial && (
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Hash className="h-3 w-3" />
                Serial Number
              </p>
              <p className="font-medium font-mono">{asset.serial}</p>
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            {asset.home_bay ? (
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Location
                </p>
                <p className="font-medium">{asset.home_bay.name}</p>
              </div>
            ) : asset.home_location_text ? (
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  Location
                </p>
                <p className="font-medium">{asset.home_location_text}</p>
              </div>
            ) : null}
            
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Added
              </p>
              <p className="font-medium">{format(new Date(asset.created_at), 'MMM d, yyyy')}</p>
            </div>
          </div>

          {asset.notes && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-muted-foreground mb-1">Notes</p>
                <p className="text-sm whitespace-pre-wrap">{asset.notes}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Financial Details - Manager Only */}
      {isManager && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Financial Details
            </CardTitle>
            <CardDescription>Manager-only information</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingFinancials ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : financials ? (
              <div className="grid grid-cols-2 gap-4">
                {financials.purchase_date && (
                  <div>
                    <p className="text-sm text-muted-foreground">Purchase Date</p>
                    <p className="font-medium">{format(new Date(financials.purchase_date), 'MMM d, yyyy')}</p>
                  </div>
                )}
                {financials.purchase_cost && (
                  <div>
                    <p className="text-sm text-muted-foreground">Purchase Cost</p>
                    <p className="font-medium">${financials.purchase_cost.toLocaleString()}</p>
                  </div>
                )}
                {financials.vendor && (
                  <div>
                    <p className="text-sm text-muted-foreground">Vendor</p>
                    <p className="font-medium">{financials.vendor}</p>
                  </div>
                )}
                {financials.warranty_expires_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Warranty Expires</p>
                    <p className="font-medium">{format(new Date(financials.warranty_expires_at), 'MMM d, yyyy')}</p>
                    {new Date(financials.warranty_expires_at) < new Date() && (
                      <Badge variant="destructive" className="mt-1 text-xs">Expired</Badge>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No financial data recorded</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Consolidated financial intelligence — manager-only, self-gated */}
      <AssetFinancialSummaryCard assetId={asset.id} />
    </div>
  );
}
